import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, ClipboardList } from "lucide-react";

const now = new Date();
const PERIODS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return `${String(d.getMonth() + 1).padStart(2, "0")}${d.getFullYear()}`;
});
const label = (p) => new Date(Number(p.slice(2)), Number(p.slice(0, 2)) - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
const TAB = { sales: "import", purchase: "import", gstr2b: "reconcile", eway_out: "eway", eway_in: "eway", challan: "ledgers" };

function Cell({ f, onClick }) {
  const tone = f.uploaded ? "text-emerald-600" : f.required ? "text-rose-500" : "text-muted-foreground";
  return (
    <button onClick={onClick} className={`flex items-center gap-1 ${tone} hover:underline`} title={f.uploaded ? `${f.count} record(s)` : f.required ? "Missing" : "Not uploaded (only if applicable)"}>
      {f.uploaded ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
      {f.uploaded ? f.count : f.required ? "Missing" : "—"}
    </button>
  );
}

export function PortalChecklist() {
  const [period, setPeriod] = useState(PERIODS[1]);
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get(`/portal-checklist?period=${period}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [period]);
  return (
    <div className="rounded-lg border border-border bg-card" data-testid="portal-checklist">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-border">
        <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-indigo-500" /><span className="font-head font-semibold">Portal download checklist</span>
          {d && <span className="text-xs text-muted-foreground font-mono" data-testid="portal-checklist-summary">{d.summary.complete}/{d.summary.clients} clients complete</span>}</div>
        <select data-testid="portal-checklist-period-select" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-md bg-card border border-border px-3 py-1.5 text-sm font-mono">
          {PERIODS.map((p) => <option key={p} value={p}>{label(p)}</option>)}
        </select>
      </div>
      {d && <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground border-b border-border"><tr>
            <th className="px-3 py-2 text-left">Client</th>
            {d.items.map((i) => <th key={i.key} className="px-3 py-2 text-left whitespace-nowrap">{i.label}{!i.required && <span className="font-normal"> (if any)</span>}</th>)}
          </tr></thead>
          <tbody>
            {d.clients.map((c) => (
              <tr key={c.client_id} className="border-b border-border/40" data-testid={`checklist-row-${c.client_id}`}>
                <td className="px-3 py-2 min-w-[170px]"><div className="font-medium">{c.legal_name}</div><div className="font-mono text-[10px] text-muted-foreground">{c.gstin}</div></td>
                {c.files.map((f) => <td key={f.key} className="px-3 py-2 font-mono" data-testid={`checklist-${c.client_id}-${f.key}`}>
                  <Cell f={f} onClick={() => nav(`/clients/${c.client_id}?tab=${TAB[f.key]}&period=${period}`)} /></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
      <p className="px-5 py-2 text-xs text-muted-foreground">Required each month: sales register, purchase register and GSTR-2B. E-way bills and challans only when applicable. Click a cell to open the upload tab.</p>
    </div>
  );
}
